import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet } from '../../utils/api'

interface Company {
  alias: string
  company_name: string
  company_logo_url?: string | null
  total_chats: number
  total_time_seconds: number
  avg_resolution_seconds: number | null
  last_chat_at: string | null
}

const SORTS: Array<[string, string]> = [
  ['latest', 'Latest activity'],
  ['oldest', 'Oldest activity'],
  ['most', 'Most chats'],
  ['least', 'Fewest chats'],
  ['most_time', 'Most time on chat'],
  ['least_time', 'Least time on chat'],
]

export function fmtDuration(sec: number | null | undefined): string {
  if (!sec || sec <= 0) return '—'
  const s = Math.round(sec)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  if (h) return `${h}h ${m}m`
  if (m) return `${m}m ${ss}s`
  return `${ss}s`
}

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-lg font-semibold text-txt-primary leading-tight">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-txt-tertiary mt-0.5">{label}</div>
  </div>
)

export const ChatHistoryCompaniesView: React.FC = () => {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Company[]>([])
  const [sort, setSort] = useState('latest')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    apiGet<{ companies: Company[] }>(`/chat-history/companies?sort=${sort}`)
      .then((d) => {
        if (active) {
          setCompanies(d.companies || [])
          setError(null)
        }
      })
      .catch(() => {
        if (active) setError('Failed to load chat history')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [sort])

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <p className="text-sm text-txt-secondary">All customer chat transcripts, grouped by company</p>
        <label className="flex items-center gap-2 text-sm text-txt-secondary">
          Sort by
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-3 py-2 rounded-lg border border-border bg-surface text-sm text-txt-primary"
          >
            {SORTS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-txt-tertiary">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : companies.length === 0 ? (
        <p className="text-sm text-txt-tertiary">No chats yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c) => (
            <button
              key={c.alias}
              onClick={() => navigate(`/chat-history/${encodeURIComponent(c.alias)}`)}
              className="text-left rounded-xl border border-border bg-surface hover:shadow-md hover:border-brand-400 transition-all p-5"
            >
              <div className="flex items-center gap-3 mb-1">
                {c.company_logo_url ? (
                  <img
                    src={c.company_logo_url}
                    alt=""
                    className="w-9 h-9 rounded-lg object-contain bg-white border border-border shrink-0"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                  />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center font-semibold shrink-0">
                    {(c.company_name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <h3 className="text-base font-semibold text-txt-primary truncate" title={c.company_name}>
                  {c.company_name}
                </h3>
              </div>
              <p className="text-[11px] text-txt-tertiary mb-4">
                {c.last_chat_at ? `Last chat ${new Date(c.last_chat_at).toLocaleDateString()}` : ' '}
              </p>
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Chats" value={String(c.total_chats)} />
                <Stat label="Total time" value={fmtDuration(c.total_time_seconds)} />
                <Stat label="Avg resolve" value={fmtDuration(c.avg_resolution_seconds)} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
